import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { 
  ClipboardCheck, Check, X, MessageSquare, FileText, Star, BookOpen, Filter, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { getBookCover } from '@/lib/assets';

const pendingTitles = [
  { id: 1, title: 'The Wonders of Coding', author: 'Sarah Chen', publisher: 'MC Edu', year: 2024, level: 'Secondary', category: 'NF EL', isbn: '978-981-12345-16', synopsis: 'An introduction to programming concepts for young learners.', compliance: ['age', 'reading', 'guidelines', 'new'], status: 'pending' },
  { id: 2, title: 'Climate Change & You', author: 'David Atkins', publisher: 'Nat. Geo', year: 2025, level: 'Secondary', category: 'NF EL', isbn: '978-1-4262-0123', synopsis: 'Understanding climate change and what young people can do.', compliance: ['age', 'reading', 'guidelines', 'new'], status: 'pending' },
  { id: 3, title: 'Psychology of Success', author: 'James Lim', publisher: 'Pearson', year: 2021, level: 'Secondary', category: 'NF EL', isbn: '978-981-09876-1', synopsis: 'A guide for teens on growth mindset and resilience.', compliance: ['age', 'reading', 'new'], warning: 'Pub year 2021 (exception required)', status: 'pending' },
  { id: 4, title: 'Singapore History Tales', author: 'Lee Wei Ming', publisher: 'NLB', year: 2024, level: 'Secondary', category: 'NF EL', isbn: '978-981-12345-20', synopsis: 'Stories from Singapore\'s rich history.', compliance: ['age', 'reading', 'guidelines', 'new'], status: 'pending' },
  { id: 5, title: 'Math Made Fun', author: 'Dr Tan Hui', publisher: 'EPH', year: 2025, level: 'Primary', category: 'NF EL', isbn: '978-981-12345-21', synopsis: 'Making mathematics enjoyable for young learners.', compliance: ['age', 'reading', 'guidelines', 'new'], status: 'pending' },
];

const rejectedTitles = [
  { id: 10, title: 'Psychology of Success', reason: 'Not age-appropriate (adult tone)', dateRejected: '25 Apr 2026', deadline: '30 Apr 2026', deadlineUrgent: true, response: 'Pending', replacement: null, replacementTitle: 'Mindset Matters: A Teen\'s Guide', replacementAuthor: 'Dr Serene Tan', replacementPublisher: 'Times', replacementYear: 2024 },
  { id: 11, title: 'Living with Purpose', reason: 'Fails MOE Guidelines (religious content)', dateRejected: '24 Apr 2026', deadline: '29 Apr 2026', deadlineUrgent: false, response: '"The Courage Code" proposed', replacement: 'The Courage Code', replacementTitle: 'The Courage Code', replacementAuthor: 'Maya Singh', replacementPublisher: 'Penguin', replacementYear: 2024 },
  { id: 12, title: 'Advanced Chemistry Sec', reason: 'Not right reading level (Pre-U content)', dateRejected: '23 Apr 2026', deadline: '28 Apr 2026', deadlineUrgent: false, response: '"Chemistry Simplified" proposed', replacement: 'Chemistry Simplified', replacementTitle: 'Chemistry Simplified', replacementAuthor: 'Prof Wong', replacementPublisher: 'MC Edu', replacementYear: 2025 },
];

const approvedTitles = [
  { id: 20, title: 'Between Two Worlds', author: 'Amelia Tan', level: 'Sec', category: 'Fiction EL', approvedDate: '20 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 21, title: 'The Memory Keeper', author: 'Siti Rahimah', level: 'Sec', category: 'Fiction EL', approvedDate: '20 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 22, title: 'Singapore Stories Vol 3', author: 'Various', level: 'All', category: 'NF EL', approvedDate: '19 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 23, title: 'Roots & Routes', author: 'David Tan', level: 'Sec', category: 'NF EL', approvedDate: '19 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
  { id: 24, title: 'Semangat!', author: 'Hanna Abdullah', level: 'Sec', category: 'MTL-ML', approvedDate: '18 Apr 2026', approvedBy: 'Ms Lim Hui Shan' },
];

const ComplianceChecklist = ({ items, warning }) => (
  <div className="flex flex-wrap gap-2">
    {items.includes('age') && (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
        <Check className="w-3 h-3 mr-1" /> Age-appropriate
      </Badge>
    )}
    {items.includes('reading') && (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
        <Check className="w-3 h-3 mr-1" /> Reading level
      </Badge>
    )}
    {items.includes('guidelines') && (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
        <Check className="w-3 h-3 mr-1" /> MOE Guidelines
      </Badge>
    )}
    {items.includes('new') && (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
        <Check className="w-3 h-3 mr-1" /> New title
      </Badge>
    )}
    {warning && (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
        ⚠️ {warning}
      </Badge>
    )}
  </div>
);

export default function MOECurationApproval() {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedTitles, setSelectedTitles] = useState([]);

  const handleApprove = (id) => {
    toast.success('Title approved successfully');
  };

  const handleReject = (id) => {
    toast.error('Title rejected');
  };

  const handleBulkApprove = () => {
    const count = selectedTitles.length > 0 ? selectedTitles.length : 29;
    toast.success(`${count} titles approved successfully`);
    setSelectedTitles([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedTitles(pendingTitles.filter(t => !t.warning).map(t => t.id));
    } else {
      setSelectedTitles([]);
    }
  };

  const handleSelectTitle = (id, checked) => {
    if (checked) {
      setSelectedTitles([...selectedTitles, id]);
    } else {
      setSelectedTitles(selectedTitles.filter(t => t !== id));
    }
  };

  const handleReviewReplacement = (title) => {
    toast.success(`Replacement "${title.replacementTitle}" approved`);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="w-7 h-7 text-[#1A3C6E]" />
            Curation Review & Approval
          </h1>
          <p className="text-slate-500 mt-1">CDS Cycle 1 2028 | Submission deadline: 30 Apr 2026</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-100 text-amber-700">34 Pending</Badge>
          <Badge className="bg-emerald-100 text-emerald-700">194 Approved</Badge>
          <Badge className="bg-red-100 text-red-700">3 Rejected</Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending Review (34)</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved (194)</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected (3)</TabsTrigger>
          <TabsTrigger value="replacement" data-testid="tab-replacement">Replacement Pending (3)</TabsTrigger>
          <TabsTrigger value="suggest" data-testid="tab-suggest">Suggest Titles</TabsTrigger>
        </TabsList>

        {/* Pending Review Tab */}
        <TabsContent value="pending" className="mt-6">
          {/* Filters & Bulk Actions */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={selectedTitles.length === pendingTitles.filter(t => !t.warning).length}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm text-slate-600">Select All</span>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select defaultValue="all">
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="primary">Primary</SelectItem>
                  <SelectItem value="secondary">Secondary</SelectItem>
                  <SelectItem value="preu">Pre-U</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Select defaultValue="all">
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="nf-el">NF EL</SelectItem>
                <SelectItem value="fiction-el">Fiction EL</SelectItem>
                <SelectItem value="mtl-cl">MTL-CL</SelectItem>
                <SelectItem value="mtl-ml">MTL-ML</SelectItem>
                <SelectItem value="mtl-tl">MTL-TL</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Search title/author..." className="w-64" />
            <div className="ml-auto flex gap-2">
              {selectedTitles.length > 0 && (
                <Button 
                  variant="outline"
                  className="text-red-600"
                  onClick={() => toast.error(`${selectedTitles.length} titles rejected`)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Bulk Reject Selected ({selectedTitles.length})
                </Button>
              )}
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleBulkApprove}
                data-testid="bulk-approve-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {selectedTitles.length > 0 
                  ? `Approve Selected (${selectedTitles.length})` 
                  : 'Approve All With No Issues (29 titles)'}
              </Button>
            </div>
          </div>

          {/* Review Cards */}
          <div className="space-y-4">
            {pendingTitles.map((title) => (
              <Card key={title.id} className={`overflow-hidden ${selectedTitles.includes(title.id) ? 'ring-2 ring-emerald-500' : ''}`}>
                <div className="flex">
                  {/* Book cover */}
                  <div className="w-32 shrink-0 relative">
                    <img 
                      src={getBookCover(title.title)} 
                      alt={title.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2">
                      <Checkbox 
                        checked={selectedTitles.includes(title.id)}
                        onCheckedChange={(checked) => handleSelectTitle(title.id, checked)}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{title.level}</Badge>
                          <Badge variant="outline">{title.category}</Badge>
                          <span className="text-xs text-slate-500">Submitted 28 Apr 2026</span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-slate-900">{title.title}</h3>
                        <p className="text-sm text-slate-600">{title.author} | {title.publisher} | {title.year}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4].map(i => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                        <Star className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-4">"{title.synopsis}"</p>
                    <p className="text-xs text-slate-500 mb-4">ISBN: {title.isbn}</p>
                    
                    <div className="flex items-center justify-between">
                      <ComplianceChecklist items={title.compliance} warning={title.warning} />
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          Query Civica
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleReject(title.id)}
                          data-testid={`reject-${title.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleApprove(title.id)}
                          data-testid={`approve-${title.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-6">
            <Button variant="outline">Show 29 more cards</Button>
          </div>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Approved Titles</CardTitle>
              </div>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export List
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Approved Date</TableHead>
                    <TableHead>Approved By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvedTitles.map((title) => (
                    <TableRow key={title.id} className="table-row-striped">
                      <TableCell className="font-medium">{title.title}</TableCell>
                      <TableCell>{title.author}</TableCell>
                      <TableCell>{title.level}</TableCell>
                      <TableCell>{title.category}</TableCell>
                      <TableCell>{title.approvedDate}</TableCell>
                      <TableCell>{title.approvedBy}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-sm text-slate-500 mt-4 text-center">Showing 5 of 194 approved titles</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Rejected Titles</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Rejection Reason</TableHead>
                    <TableHead>Date Rejected</TableHead>
                    <TableHead>Replacement Deadline</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rejectedTitles.map((title) => (
                    <TableRow key={title.id} className="table-row-striped">
                      <TableCell className="font-medium">{title.title}</TableCell>
                      <TableCell className="text-red-600">{title.reason}</TableCell>
                      <TableCell>{title.dateRejected}</TableCell>
                      <TableCell className={title.deadlineUrgent ? 'text-red-600 font-semibold' : ''}>
                        {title.deadline} {title.deadlineUrgent && '⚠️'}
                      </TableCell>
                      <TableCell>
                        {title.replacement ? (
                          <Badge className="bg-emerald-50 text-emerald-700">Replacement received</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Replacement Pending Tab */}
        <TabsContent value="replacement" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-red-700">Replacement Required — Review Now</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {rejectedTitles.map((title) => (
                  <div key={title.id} className={`p-4 border rounded-lg ${title.deadlineUrgent ? 'border-red-200 bg-red-50/50' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-slate-900">Original: "{title.title}"</h4>
                          {title.deadlineUrgent && (
                            <Badge variant="destructive">DUE TODAY</Badge>
                          )}
                        </div>
                        <p className="text-sm text-red-600 mb-2">Rejected: {title.reason}</p>
                        <p className="text-sm text-slate-500">Deadline: {title.deadline}</p>
                        
                        {/* Replacement Info */}
                        <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <p className="text-sm font-medium text-emerald-800 mb-1">Proposed Replacement:</p>
                          <p className="font-semibold text-emerald-900">"{title.replacementTitle}"</p>
                          <p className="text-sm text-emerald-700">{title.replacementAuthor} | {title.replacementPublisher} | {title.replacementYear}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" /> Age-appropriate
                            </Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" /> Reading level
                            </Badge>
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                              <Check className="w-3 h-3 mr-1" /> MOE Guidelines
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleReviewReplacement(title)}
                          data-testid={`approve-replacement-${title.id}`}
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approve Replacement
                        </Button>
                        <Button variant="outline" className="text-red-600">
                          <X className="w-4 h-4 mr-2" />
                          Reject — Request New
                        </Button>
                        <Button variant="outline">
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Query Civica
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Bulk Action */}
              <div className="mt-6 flex gap-2">
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => toast.success('All 3 replacements approved')}
                  data-testid="bulk-approve-replacements"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve All 3 Replacements
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suggest Titles Tab */}
        <TabsContent value="suggest" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Suggest Titles for CDS Cycle 2 2028</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Input placeholder="Title" />
                <Input placeholder="Author" />
                <Input placeholder="Publisher" />
                <Input placeholder="Year" />
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="preu">Pre-U</SelectItem>
                  </SelectContent>
                </Select>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nf-el">NF EL</SelectItem>
                    <SelectItem value="fiction-el">Fiction EL</SelectItem>
                    <SelectItem value="mtl">MTL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Reason for suggestion..." className="mb-4" />
              <Button className="bg-[#1A3C6E] hover:bg-[#142E54]">Submit Suggestion</Button>
              
              <div className="mt-8">
                <h4 className="font-semibold text-slate-900 mb-4">Submitted suggestions (15 received)</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">Girls Who Code</span>
                      <span className="text-sm text-slate-500 ml-2">Suggested by RGS Library | Secondary</span>
                    </div>
                    <Badge className="bg-emerald-100 text-emerald-700">Suitable — Add to Cycle 2</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">Malay Proverbs for Kids</span>
                      <span className="text-sm text-slate-500 ml-2">Suggested by Queenstown Pri | Primary</span>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">Under Civica review</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
